import { useState, useEffect, useRef, type ReactNode } from 'react';
import { Wifi, WifiOff, Shield } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useUIStore } from '@/stores/uiStore';
import { useSettingsStore, type ModelProvider } from '@/stores/settingsStore';
import { useChatStore } from '@/stores/chatStore';
import gsap from 'gsap';
import clsx from 'clsx';
import arcReactorIcon from '@/assets/arc-reactor-icon.png';

interface StatusBarProps {
  // When set, appended after the JARVIS label as a subtitle
  // (e.g. "MISSION CONTROL"). Used by Mission Control.
  subtitle?: string;
  // Optional ReactNode rendered in the right capsule before the
  // model/voice/time stack. Used by Mission Control for World Model
  // confidence and Last Sync.
  rightExtras?: ReactNode;
  // When true, clicking the JARVIS title navigates to "/" instead of
  // resetting the chat in-place. Used on pages like Mission Control
  // where there's no chat to reset.
  homeOnLogoClick?: boolean;
}

const providerLabels: Record<ModelProvider, string> = {
  claude: 'Claude',
  gemini: 'Gemini',
  cerebras: 'Cerebras',
  stark_protocol: 'Stark',
};

const providerColors: Record<ModelProvider, string> = {
  claude: '#ff8c00',
  gemini: '#4285F4',
  cerebras: '#a855f7',
  stark_protocol: '#00d4ff',
};

function MiniSpectrum({ active }: { active: boolean }) {
  return (
    <div className="flex items-center gap-[1px] h-3">
      {Array.from({ length: 5 }).map((_, i) => (
        <div
          key={i}
          className="w-[2px] rounded-full transition-all duration-500"
          style={{
            height: active ? `${40 + Math.sin(Date.now() / 300 + i) * 40}%` : '20%',
            background: active ? 'rgba(0, 212, 255, 0.5)' : 'rgba(255,255,255,0.08)',
            animationDelay: `${i * 0.1}s`,
          }}
        />
      ))}
    </div>
  );
}

export default function StatusBar({
  subtitle,
  rightExtras,
  homeOnLogoClick,
}: StatusBarProps = {}) {
  const navigate = useNavigate();
  const wsConnected = useUIStore((s) => s.wsConnected);
  const isThinking = useUIStore((s) => s.isThinking);
  const isSpeaking = useUIStore((s) => s.isSpeaking);
  const isListening = useUIStore((s) => s.isListening);
  const activity = useUIStore((s) => s.jarvisActivity);
  const modelPreference = useSettingsStore((s) => s.modelPreference);
  const voiceEnabled = useSettingsStore((s) => s.voiceEnabled);
  const use24HourTime = useSettingsStore((s) => s.use24HourTime);

  const [time, setTime] = useState('');
  const [date, setDate] = useState('');
  const leftRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const update = () => {
      const now = new Date();
      setTime(
        now.toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: !use24HourTime,
        }),
      );
      setDate(
        now.toLocaleDateString([], {
          month: 'short',
          day: 'numeric',
        }),
      );
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [use24HourTime]);

  // GSAP boot animation
  useEffect(() => {
    if (leftRef.current && rightRef.current) {
      gsap.fromTo(
        leftRef.current,
        { opacity: 0, x: -20, scale: 0.95 },
        { opacity: 1, x: 0, scale: 1, duration: 0.6, ease: 'power3.out', delay: 0.1 },
      );
      gsap.fromTo(
        rightRef.current,
        { opacity: 0, x: 20, scale: 0.95 },
        { opacity: 1, x: 0, scale: 1, duration: 0.6, ease: 'power3.out', delay: 0.15 },
      );
    }
  }, []);

  const status = isThinking
    ? 'PROCESSING'
    : isSpeaking
      ? 'SPEAKING'
      : isListening
        ? 'LISTENING'
        : 'STANDBY';

  const statusClass = isThinking
    ? 'processing'
    : isSpeaking
      ? 'warning'
      : isListening
        ? 'processing'
        : 'online';

  const modelLabel = providerLabels[modelPreference] || modelPreference;
  const modelColor = providerColors[modelPreference] || '#00d4ff';

  const openModelPicker = () => {
    window.dispatchEvent(new CustomEvent('jarvis-model-toggle'));
  };

  return (
    <div className="fixed top-4 left-4 right-4 z-30 flex justify-between items-start pointer-events-none">
      {/* Left capsule — Logo + Status + Activity */}
      <div ref={leftRef} className="glass-capsule pointer-events-auto h-10 px-4 flex items-center gap-2.5 opacity-0">
        <button
          onClick={() => {
            if (homeOnLogoClick) {
              navigate('/');
              return;
            }
            useChatStore.getState().setCurrentConversation(null);
            useChatStore.getState().setMessages([]);
          }}
          className="flex items-center gap-2.5 hover:opacity-80 transition-opacity cursor-pointer"
          title={homeOnLogoClick ? 'New conversation' : 'New conversation'}
        >
          <img
            src={arcReactorIcon}
            alt="Arc Reactor"
            className="w-5 h-5 flex-shrink-0 object-contain"
          />
          <span className="font-display text-[11px] font-bold tracking-[0.2em] text-jarvis-blue glow-text hidden sm:block">
            J.A.R.V.I.S.
          </span>
          {subtitle && (
            <span className="hidden md:block text-[9px] font-mono tracking-[0.3em] text-jarvis-blue/50 ml-1">
              · {subtitle}
            </span>
          )}
        </button>

        <div className="w-px h-4 bg-white/[0.06]" />

        <div className="flex items-center gap-1.5">
          <div className={clsx('status-dot', statusClass)} />
          <span className="text-[10px] font-mono text-gray-400 tracking-wider">{status}</span>
        </div>

        <div className="w-px h-4 bg-white/[0.06] hidden sm:block" />

        {/* Activity indicator — click to open diagnostics */}
        <button
          onClick={() => window.dispatchEvent(new CustomEvent('jarvis-diagnostics-toggle'))}
          className="hidden sm:flex items-center gap-1.5 hover:opacity-80 transition-opacity cursor-pointer"
          title="Open Diagnostics"
        >
          <MiniSpectrum active={activity > 0.3} />
          <span className="text-[9px] font-mono text-jarvis-blue/40 tabular-nums">
            {Math.round(activity * 100)}%
          </span>
        </button>

        {/* Security badge */}
        <div className="hidden sm:flex items-center gap-1">
          <Shield size={10} className="text-hud-green/50" />
          <span className="text-[8px] font-mono text-hud-green/40">SEC</span>
        </div>
      </div>

      {/* Center label — subtle floating text */}
      <div className="hidden lg:flex items-center gap-2 mt-3 opacity-0 boot-1" style={{ animation: 'floatIn 0.5s cubic-bezier(0.16, 1, 0.3, 1) 0.4s both' }}>
        <span className="text-[9px] font-mono tracking-[0.35em] text-jarvis-blue/20 uppercase">
          STARK INDUSTRIES SECURE SERVER
        </span>
      </div>

      {/* Right capsule — Model + Time + Connection */}
      <div ref={rightRef} className="glass-capsule pointer-events-auto h-10 px-4 flex items-center gap-3 opacity-0">
        {rightExtras && (
          <>
            {rightExtras}
            <div className="w-px h-4 bg-white/[0.06]" />
          </>
        )}
        {/* Current model badge — clickable */}
        <button
          onClick={openModelPicker}
          className="flex items-center gap-1.5 hover:opacity-80 transition-opacity"
        >
          <div
            className="w-1.5 h-1.5 rounded-full"
            style={{
              backgroundColor: modelColor,
              boxShadow: `0 0 6px ${modelColor}66`,
            }}
          />
          <span className="text-[10px] font-mono tracking-wider" style={{ color: modelColor }}>
            {modelLabel}
          </span>
        </button>

        <div className="w-px h-4 bg-white/[0.06]" />

        {/* Voice indicator */}
        <div className="flex items-center gap-1">
          <div
            className={clsx('w-1 h-1 rounded-full', {
              'bg-jarvis-gold shadow-[0_0_4px_rgba(168,85,247,0.5)]': voiceEnabled,
              'bg-gray-700': !voiceEnabled,
            })}
          />
          <span className="text-[8px] font-mono text-gray-600 tracking-wider hidden sm:block">
            {voiceEnabled ? 'VOX' : 'MUTE'}
          </span>
        </div>

        <div className="w-px h-4 bg-white/[0.06]" />

        {/* Date + Time */}
        <div className="flex flex-col items-end gap-1 hidden sm:flex">
          <span className="text-[9px] font-mono text-gray-500 tracking-wider leading-none">
            {date}
          </span>
          <span className="font-mono text-[11px] text-gray-300 tracking-wider tabular-nums leading-none">
            {time}
          </span>
        </div>
        <span className="font-mono text-[11px] text-gray-300 tracking-wider tabular-nums sm:hidden">
          {time}
        </span>

        <div className="w-px h-4 bg-white/[0.06]" />

        {/* Connection — click to view active sessions */}
        <button
          onClick={() => window.dispatchEvent(new CustomEvent('jarvis-sessions-manage-toggle'))}
          className="relative group flex items-center gap-1.5 hover:opacity-80 transition-opacity cursor-pointer"
          title="View Active Sessions"
        >
          {wsConnected ? (
            <Wifi size={12} className="text-hud-green" />
          ) : (
            <WifiOff size={12} className="text-gray-600" />
          )}
          <span
            className="text-[9px] font-mono tracking-wider"
            style={{ color: wsConnected ? '#39ff14' : '#666' }}
          >
            {wsConnected ? 'ONLINE' : 'OFFLINE'}
          </span>
          {/* Removed tooltip — was showing as artifact line on hover */}
        </button>
      </div>
    </div>
  );
}
